/**
 * Créer une version de contre-étude à partir d'une autre.
 *
 * Le geste du PRD §4.2 : deux ou trois scénarios d'un même chantier, qu'on compare
 * avant de retenir celui qu'on présente. Demandé par Chris le 10/08/2026 —
 * *« soit à partir de la version figée soit à partir d'une autre version, on doit
 * pouvoir choisir »*.
 *
 * Deux partis pris d'affichage :
 *
 *  - **le formulaire est replié par défaut.** Créer une version est un geste rare
 *    et conséquent ; un champ de saisie ouvert en permanence au-dessus du déboursé
 *    ferait croire qu'il attend quelque chose ;
 *  - **la source est pré-remplie sur la version affichée**, parce que c'est le cas
 *    de très loin le plus fréquent : on regarde une contre-étude et on veut en
 *    essayer une variante. Le choix reste ouvert pour repartir du devis vendu.
 */
import { useEffect, useState } from "react";
import type { JSX } from "react";
import { deriverVersion } from "./api";

export type Source = {
  readonly id: string;
  readonly libelle: string;
};

type Etat = { phase: "repos" } | { phase: "envoi" } | { phase: "erreur"; raison: string };

export function NouvelleVersion({
  sources,
  sourceParDefaut,
  onCreee,
}: {
  readonly sources: readonly Source[];
  readonly sourceParDefaut: string;
  /** Basculer sur la version qui vient d'être créée. */
  readonly onCreee: (versionId: string) => void;
}): JSX.Element | null {
  const [ouvert, setOuvert] = useState(false);
  const [sourceId, setSourceId] = useState(sourceParDefaut);
  const [libelle, setLibelle] = useState("");
  const [etat, setEtat] = useState<Etat>({ phase: "repos" });

  // La version affichée a changé pendant que le formulaire était replié : la
  // source suit, sinon on dériverait d'une version qu'on ne regarde plus.
  useEffect(() => {
    if (!ouvert) setSourceId(sourceParDefaut);
  }, [sourceParDefaut, ouvert]);

  if (sources.length === 0) return null;

  const complet = libelle.trim() !== "" && sourceId !== "";
  const envoiEnCours = etat.phase === "envoi";

  if (!ouvert) {
    return (
      <button
        type="button"
        className="lien"
        onClick={() => {
          setOuvert(true);
        }}
      >
        Créer une version à partir de…
      </button>
    );
  }

  const creer = (): void => {
    if (!complet) return;
    setEtat({ phase: "envoi" });

    deriverVersion(sourceId, libelle.trim())
      .then(({ version }) => {
        setEtat({ phase: "repos" });
        setOuvert(false);
        setLibelle("");
        onCreee(version.id);
      })
      .catch((erreur: unknown) => {
        setEtat({
          phase: "erreur",
          raison: erreur instanceof Error ? erreur.message : String(erreur),
        });
      });
  };

  return (
    <form
      className="nouvelle-version"
      onSubmit={(e) => {
        e.preventDefault();
        creer();
      }}
    >
      <label>
        À partir de
        <select
          value={sourceId}
          disabled={envoiEnCours}
          onChange={(e) => {
            setSourceId(e.target.value);
          }}
        >
          {sources.map((s) => (
            <option key={s.id} value={s.id}>
              {s.libelle}
            </option>
          ))}
        </select>
      </label>

      <label>
        Nom de la version
        <input
          value={libelle}
          placeholder="Variante sans sous-traitance"
          disabled={envoiEnCours}
          autoFocus
          onChange={(e) => {
            setLibelle(e.target.value);
            if (etat.phase === "erreur") setEtat({ phase: "repos" });
          }}
        />
      </label>

      <div className="nouvelle-version__actions">
        <button type="submit" className="principal" disabled={!complet || envoiEnCours}>
          {envoiEnCours ? "Création…" : "Créer"}
        </button>
        <button
          type="button"
          disabled={envoiEnCours}
          onClick={() => {
            setOuvert(false);
            setLibelle("");
            setEtat({ phase: "repos" });
          }}
        >
          Annuler
        </button>
      </div>

      {etat.phase === "erreur" && (
        <p className="alerte" role="alert">
          {etat.raison}
        </p>
      )}

      <p className="etat">
        La nouvelle version reprend l'état de sa source à cet instant — quantités,
        prix, lignes remplacées ou retirées, postes écartés. Elle est modifiable, et la source
        n'est pas touchée.
      </p>
    </form>
  );
}
